import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Box,
  CircularProgress,
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  History as HistoryIcon,
} from '@mui/icons-material'
import { BackupConfiguracao } from '../../api/backups'
import { Empresa } from '../../types/api'
import JsonDetailDialog from '../common/JsonDetailDialog'
import dayjs from 'dayjs'

interface BackupsTableProps {
  backups: BackupConfiguracao[]
  empresas: Empresa[]
  isLoading?: boolean
  onEdit: (backup: BackupConfiguracao) => void
  onDelete: (backup: BackupConfiguracao) => void
  onExecutar: (backup: BackupConfiguracao) => void
  onVerExecucoes: (backup: BackupConfiguracao) => void
}

const BackupsTable: React.FC<BackupsTableProps> = ({
  backups,
  empresas,
  isLoading,
  onEdit,
  onDelete,
  onExecutar,
  onVerExecucoes,
}) => {
  const getEmpresaNome = (idEmpresa: number | null): string => {
    if (idEmpresa === null) return 'Galatea Web'
    const empresa = empresas.find((e) => e.id === idEmpresa)
    return empresa?.nome || `Empresa ${idEmpresa}`
  }

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'concluido':
        return 'success'
      case 'em_progresso':
        return 'primary'
      case 'falhou':
        return 'error'
      case 'pendente':
        return 'warning'
      default:
        return 'default'
    }
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-'
    try {
      return dayjs(dateString).format('DD/MM/YYYY HH:mm')
    } catch {
      return dateString
    }
  }

  const formatSize = (bytes: number | null): string => {
    if (!bytes) return '-'
    const mb = bytes / (1024 * 1024)
    return `${mb.toFixed(2)} MB`
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (backups.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        Nenhuma configuração de backup encontrada
      </Box>
    )
  }

  const [detailDialog, setDetailDialog] = React.useState<{ open: boolean; content?: string | null; title?: string }>({
    open: false,
  })

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Empresa</TableCell>
            <TableCell>Nome</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Configuração</TableCell>
            <TableCell>Cron</TableCell>
            <TableCell>Ativo</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Última Execução</TableCell>
            <TableCell>Próxima Execução</TableCell>
            <TableCell>Tamanho</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {backups.map((backup) => (
            <TableRow key={backup.id}>
              <TableCell>{getEmpresaNome(backup.id_empresa)}</TableCell>
              <TableCell>{backup.nome_configuracao}</TableCell>
              <TableCell>
                <Chip
                  label={backup.tipo_backup === 'completo' ? 'Completo' : 'Incremental'}
                  size="small"
                  color={backup.tipo_backup === 'completo' ? 'primary' : 'secondary'}
                />
              </TableCell>
              <TableCell>
                {backup.configuracao_incremental ? (
                  <Box
                    component="span"
                    sx={{
                      color: 'primary.main',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={() =>
                      setDetailDialog({
                        open: true,
                        content: JSON.stringify(backup.configuracao_incremental),
                        title: `Configuração - ${backup.nome_configuracao}`,
                      })
                    }
                  >
                    Ver JSON
                  </Box>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>
                <Tooltip title={backup.cron_expression}>
                  <span>{backup.cron_expression}</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Chip
                  label={backup.ativo ? 'Sim' : 'Não'}
                  size="small"
                  color={backup.ativo ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={backup.status}
                  size="small"
                  color={getStatusColor(backup.status)}
                />
              </TableCell>
              <TableCell>{formatDate(backup.ultima_execucao_at)}</TableCell>
              <TableCell>{formatDate(backup.proxima_execucao_at)}</TableCell>
              <TableCell>{formatSize(backup.size_bytes)}</TableCell>
              <TableCell align="right">
                <Tooltip title="Executar agora">
                  <IconButton size="small" onClick={() => onExecutar(backup)}>
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Ver histórico">
                  <IconButton size="small" onClick={() => onVerExecucoes(backup)}>
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => onEdit(backup)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Deletar">
                  <IconButton size="small" onClick={() => onDelete(backup)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <JsonDetailDialog
        open={detailDialog.open}
        rawContent={detailDialog.content}
        title={detailDialog.title ?? 'Detalhes do backup'}
        onClose={() => setDetailDialog({ open: false })}
      />
    </TableContainer>
  )
}

export default BackupsTable

